import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Card, Input, Button, Radio, Typography, message, Spin, Progress, Icon, Row, Col, Form } from 'antd';
import { getData, createData } from '../../util/API';

const { Title, Text, Paragraph } = Typography;

const UserProfiling = () => {
  const history = useHistory();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showWelcome, setShowWelcome] = useState(true);
  const [showThankYou, setShowThankYou] = useState(false);
  const [userDataExists, setUserDataExists] = useState(false);
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    // Check if user data exists in localStorage
    const storedUuid = localStorage.getItem('user_uuid');
    const storedPhone = localStorage.getItem('user_phone');
    setUserDataExists(storedUuid && storedPhone);
    
    // If phone exists in localStorage, pre-fill it
    if (storedPhone) {
      setPhone(storedPhone);
    }
    
    fetchQuestionnaire();
  }, []);

  const fetchQuestionnaire = async () => {
    setLoading(true);
    try {
      const response = await getData('/partner/questionnaire/');
      console.log('Questionnaire Response:', response.data);
      
      if (response.data && response.data.questionnaire) {
        const questionnaireData = response.data.questionnaire;
        setQuestions(questionnaireData);
        
        const initialAnswers = {};
        questionnaireData.forEach(q => {
          initialAnswers[q.question_code] = '';
        });
        setAnswers(initialAnswers);
        
      } else {
        message.error('No questionnaire available');
      }
    } catch (error) {
      console.error('Error fetching questionnaire:', error);
      message.error('Failed to load questionnaire');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!fullName || fullName.trim() === '') {
      errors.fullName = 'Full name is required';
    }
    
    if (!phone || phone.trim() === '') {
      errors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s\-\(\)]{10,}$/.test(phone.trim())) {
      errors.phone = 'Please enter a valid phone number';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAnswerChange = (questionCode, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionCode]: value
    }));

    const currentQuestion = questions[currentQuestionIndex];
    const questionType = getQuestionType(currentQuestion);
    
    if (['yes-no', 'single-choice', 'number'].includes(questionType)) {
      setTimeout(() => {
        handleNextQuestion(true);
      }, 600);
    }
  };

  const handleStartQuestionnaire = () => {
    if (!userDataExists) {
      if (!validateForm()) {
        return;
      }
      // Store the entered data temporarily
      localStorage.setItem('temp_user_name', fullName.trim());
      localStorage.setItem('temp_user_phone', phone.trim());
    }
    setShowWelcome(false);
    setCurrentQuestionIndex(0);
  };

  const handleNextQuestion = (isAutoAdvance = false) => {
    const currentQuestion = questions[currentQuestionIndex];
    const currentAnswer = answers[currentQuestion.question_code];

    if (!isAutoAdvance && (!currentAnswer || currentAnswer.toString().trim() === '')) {
      message.warning('Please provide an answer before continuing');
      return;
    }

    if (currentQuestionIndex === questions.length - 1) {
      return;
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    const unansweredQuestions = questions.filter(q => 
      !answers[q.question_code] || answers[q.question_code].toString().trim() === ''
    );
    
    if (unansweredQuestions.length > 0) {
      message.error('Please answer all questions before submitting');
      return;
    }

    setSubmitting(true);
    try {
      // Prepare user data
      let userData = {};
      
      if (userDataExists) {
        // Use existing localStorage data
        userData = {
          user_uuid: localStorage.getItem('user_uuid'),
          user_phone: localStorage.getItem('user_phone')
        };
      } else {
        // Use the form data entered by user
        userData = {
          full_name: localStorage.getItem('temp_user_name') || fullName.trim(),
          phone: localStorage.getItem('temp_user_phone') || phone.trim()
        };
      }

      const submissionData = {
        ...userData,
        answers: Object.keys(answers).map(questionCode => ({
          question_code: questionCode,
          answer: answers[questionCode]
        })),
        submitted_at: new Date().toISOString()
      };
      
      console.log('Submitting answers:', submissionData);
      
      await createData('/partner/questionnaire/submit/', submissionData);
      
      // Clean up temporary data
      if (!userDataExists) {
        localStorage.removeItem('temp_user_name');
        localStorage.removeItem('temp_user_phone');
      }
      
      setShowThankYou(true);
      
      setTimeout(() => {
        history.push('/dashboard');
      }, 3000);
      
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
      message.error('Failed to submit questionnaire. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getQuestionType = (question) => {
    if (!question.options || question.options.length === 0) return 'text';
    
    const answerTypes = question.options.map(opt => opt.answer_type);
    
    if (answerTypes.every(type => type === 'yes-no')) {
      return 'yes-no';
    }
    
    if (answerTypes.every(type => type === 'number')) {
      return 'number';
    }
    
    if (question.options.length > 1) {
      return 'single-choice';
    }
    
    return 'text';
  };

  const renderQuestionInput = (question) => {
    const value = answers[question.question_code] || '';
    const questionType = getQuestionType(question);
    
    switch (questionType) {
      case 'yes-no':
        return (
          <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
            {['Yes', 'No'].map((option) => (
              <div
                key={option}
                onClick={() => handleAnswerChange(question.question_code, option)}
                style={{
                  padding: '16px 20px',
                  border: value === option ? '2px solid #ff5722' : '1px solid #cbd5e1',
                  borderRadius: '6px',
                  background: value === option ? '#fff5f5' : '#ffffff',
                  color: value === option ? '#ff5722' : '#334155',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontSize: '16px',
                  fontWeight: value === option ? '600' : '400',
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (value !== option) {
                    e.target.style.borderColor = '#ff5722';
                    e.target.style.backgroundColor = '#fef7f7';
                  }
                }}
                onMouseLeave={(e) => {
                  if (value !== option) {
                    e.target.style.borderColor = '#cbd5e1';
                    e.target.style.backgroundColor = '#ffffff';
                  }
                }}
              >
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  border: value === option ? '6px solid #ff5722' : '2px solid #cbd5e1',
                  marginRight: '12px',
                  transition: 'all 0.2s ease'
                }} />
                {option}
              </div>
            ))}
          </div>
        );

      case 'number':
      case 'single-choice':
        return (
          <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
            {question.options.map((option, index) => (
              <div
                key={index}
                onClick={() => handleAnswerChange(question.question_code, option.text)}
                style={{
                  padding: '14px 16px',
                  border: value === option.text ? '2px solid #ff5722' : '1px solid #cbd5e1',
                  borderRadius: '6px',
                  background: value === option.text ? '#fff5f5' : '#ffffff',
                  color: value === option.text ? '#ff5722' : '#334155',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontSize: '15px',
                  fontWeight: value === option.text ? '600' : '400',
                  display: 'flex',
                  alignItems: 'center'
                }}
                onMouseEnter={(e) => {
                  if (value !== option.text) {
                    e.target.style.borderColor = '#ff5722';
                    e.target.style.backgroundColor = '#fef7f7';
                  }
                }}
                onMouseLeave={(e) => {
                  if (value !== option.text) {
                    e.target.style.borderColor = '#cbd5e1';
                    e.target.style.backgroundColor = '#ffffff';
                  }
                }}
              >
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  border: value === option.text ? '5px solid #ff5722' : '2px solid #cbd5e1',
                  marginRight: '12px',
                  transition: 'all 0.2s ease'
                }} />
                {option.text} {option.answer_type === 'number' ? 'years' : ''}
              </div>
            ))}
          </div>
        );

      default:
        return (
          <div>
            <Input
              placeholder="Type your answer here..."
              value={value}
              onChange={(e) => handleAnswerChange(question.question_code, e.target.value)}
              size="large"
              style={{ 
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                fontSize: '16px',
                padding: '12px 16px',
                height: '48px',
                boxShadow: 'none',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#ff5722';
                e.target.style.boxShadow = '0 0 0 3px rgba(255, 87, 34, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#cbd5e1';
                e.target.style.boxShadow = 'none';
              }}
              onPressEnter={() => handleNextQuestion()}
            />
            <Text style={{ 
              fontSize: '13px', 
              color: '#64748b', 
              marginTop: '8px', 
              display: 'block'
            }}>
              Press Enter to continue
            </Text>
          </div>
        );
    }
  };

  const getProgress = () => {
    if (showWelcome) return 0;
    return Math.round(((currentQuestionIndex + 1) / questions.length) * 100);
  };

  const getCurrentQuestion = () => {
    return questions[currentQuestionIndex];
  };

  const isCurrentQuestionAnswered = () => {
    const currentQuestion = getCurrentQuestion();
    if (!currentQuestion) return false;
    const currentAnswer = answers[currentQuestion.question_code];
    return currentAnswer && currentAnswer.toString().trim() !== '';
  };

  const isLastQuestion = () => {
    return currentQuestionIndex === questions.length - 1;
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: '#f8fafc'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px', color: '#64748b', fontSize: '16px' }}>Loading assessment...</p>
        </div>
      </div>
    );
  }

  // Welcome Screen
  if (showWelcome) {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{ 
          maxWidth: '500px',
          width: '100%',
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{ 
            padding: '40px 32px 32px',
            textAlign: 'center',
            borderBottom: '1px solid #e2e8f0'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: '#ff5722',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <Icon 
                type="form" 
                style={{ 
                  fontSize: '32px', 
                  color: 'white' 
                }} 
              />
            </div>
            <Title level={1} style={{ 
              color: '#1e293b',
              margin: '0 0 8px 0',
              fontSize: '28px',
              fontWeight: '600'
            }}>
              Profiling Assessment
            </Title>
            <Text style={{ fontSize: '16px', color: '#64748b' }}>
              {userDataExists 
                ? 'Help us understand your technical background'
                : 'Please provide your details to get started'
              }
            </Text>
          </div>
          
          {/* Content */}
          <div style={{ padding: '32px' }}>
            {userDataExists ? (
              <>
                <Row gutter={24} style={{ marginBottom: '32px' }}>
                  <Col span={8} style={{ textAlign: 'center' }}>
                    <div style={{ 
                      color: '#ff5722', 
                      fontSize: '24px', 
                      fontWeight: '700',
                      marginBottom: '4px'
                    }}>
                      {questions.length}
                    </div>
                    <div style={{ color: '#64748b', fontSize: '14px' }}>Questions</div>
                  </Col>
                  <Col span={8} style={{ textAlign: 'center' }}>
                    <div style={{ 
                      color: '#ff5722', 
                      fontSize: '24px', 
                      fontWeight: '700',
                      marginBottom: '4px'
                    }}>
                      ~3
                    </div>
                    <div style={{ color: '#64748b', fontSize: '14px' }}>Minutes</div>
                  </Col>
                  <Col span={8} style={{ textAlign: 'center' }}>
                    <Icon type="safety-certificate" style={{ color: '#ff5722', fontSize: '30px', marginBottom: '4px' }} />
                    <div style={{ color: '#64748b', fontSize: '14px' }}>Secure</div>
                  </Col>
                </Row>

                <div style={{ 
                  background: '#fef7f7',
                  border: '1px solid #fed7d7',
                  borderRadius: '6px',
                  padding: '16px',
                  marginBottom: '24px'
                }}>
                  <Text style={{ color: '#c53030', fontSize: '14px', fontWeight: '500' }}>
                    <Icon type="info-circle" style={{ marginRight: '8px' }} />
                    Select your answers to continue automatically
                  </Text>
                </div>

                <Button
                  type="primary"
                  size="large"
                  onClick={handleStartQuestionnaire}
                  block
                  style={{
                    height: '48px',
                    borderRadius: '6px',
                    fontSize: '16px',
                    fontWeight: '600',
                    background: '#ff5722',
                    borderColor: '#ff5722',
                    boxShadow: 'none'
                  }}
                >
                  Get Started
                </Button>
              </>
            ) : (
              <>
                <div style={{ marginBottom: '24px' }}>
                  <Text style={{ 
                    color: '#1e293b', 
                    fontSize: '16px', 
                    fontWeight: '600',
                    display: 'block',
                    marginBottom: '8px'
                  }}>
                    Your Information
                  </Text>
                  <Text style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.6' }}>
                    We need some basic information to personalize your assessment experience.
                  </Text>
                </div>

                {/* Full Name Input */}
                <div style={{ marginBottom: '20px' }}>
                  <Text style={{ 
                    color: '#374151', 
                    fontSize: '14px', 
                    fontWeight: '500',
                    display: 'block',
                    marginBottom: '6px'
                  }}>
                    Full Name *
                  </Text>
                  <Input
                    placeholder="Enter your full name"
                    value={fullName}
                    onChange={(e) => {
                      setFullName(e.target.value);
                      if (formErrors.fullName) {
                        setFormErrors(prev => ({ ...prev, fullName: '' }));
                      }
                    }}
                    size="large"
                    style={{ 
                      borderRadius: '6px',
                      border: formErrors.fullName ? '1px solid #dc2626' : '1px solid #cbd5e1',
                      fontSize: '16px',
                      padding: '12px 16px',
                      height: '48px',
                      boxShadow: 'none',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#ff5722';
                      e.target.style.boxShadow = '0 0 0 3px rgba(255, 87, 34, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = formErrors.fullName ? '#dc2626' : '#cbd5e1';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  {formErrors.fullName && (
                    <Text style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {formErrors.fullName}
                    </Text>
                  )}
                </div>

                {/* Phone Input */}
                <div style={{ marginBottom: '24px' }}>
                  <Text style={{ 
                    color: '#374151', 
                    fontSize: '14px', 
                    fontWeight: '500',
                    display: 'block',
                    marginBottom: '6px'
                  }}>
                    Phone Number *
                  </Text>
                  <Input
                    placeholder="Enter your phone number"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value);
                      if (formErrors.phone) {
                        setFormErrors(prev => ({ ...prev, phone: '' }));
                      }
                    }}
                    size="large"
                    style={{ 
                      borderRadius: '6px',
                      border: formErrors.phone ? '1px solid #dc2626' : '1px solid #cbd5e1',
                      fontSize: '16px',
                      padding: '12px 16px',
                      height: '48px',
                      boxShadow: 'none',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#ff5722';
                      e.target.style.boxShadow = '0 0 0 3px rgba(255, 87, 34, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = formErrors.phone ? '#dc2626' : '#cbd5e1';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  {formErrors.phone && (
                    <Text style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {formErrors.phone}
                    </Text>
                  )}
                </div>

                <div style={{ 
                  background: '#fef7f7',
                  border: '1px solid #fed7d7',
                  borderRadius: '6px',
                  padding: '16px',
                  marginBottom: '24px'
                }}>
                  <Text style={{ color: '#c53030', fontSize: '14px', fontWeight: '500' }}>
                    <Icon type="info-circle" style={{ marginRight: '8px' }} />
                    This information will be included with your assessment responses
                  </Text>
                </div>

                <Button
                  type="primary"
                  size="large"
                  onClick={handleStartQuestionnaire}
                  block
                  disabled={!fullName.trim() || !phone.trim()}
                  style={{
                    height: '48px',
                    borderRadius: '6px',
                    fontSize: '16px',
                    fontWeight: '600',
                    background: (fullName.trim() && phone.trim()) ? '#ff5722' : '#cbd5e1',
                    borderColor: (fullName.trim() && phone.trim()) ? '#ff5722' : '#cbd5e1',
                    boxShadow: 'none'
                  }}
                >
                  Start Assessment
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Thank You Screen
  if (showThankYou) {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{ 
          maxWidth: '450px',
          width: '100%',
          background: 'white',
          borderRadius: '8px',
          padding: '48px 32px',
          textAlign: 'center',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ 
            width: '80px', 
            height: '80px', 
            borderRadius: '50%', 
            background: '#00bf63',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <Icon type="check" style={{ fontSize: '40px', color: 'white' }} />
          </div>
          
          <Title level={1} style={{ 
            color: '#1e293b',
            margin: '0 0 12px 0',
            fontSize: '24px',
            fontWeight: '600'
          }}>
            Thank you!
          </Title>
          
          <Text style={{ 
            fontSize: '16px',
            color: '#64748b',
            display: 'block',
            marginBottom: '24px',
            lineHeight: '1.5'
          }}>
            Your assessment has been submitted successfully. We'll review your responses and get back to you.
          </Text>

          <div style={{ 
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '6px',
            padding: '12px',
            marginBottom: '16px'
          }}>
            <Text style={{ color: '#166534', fontSize: '14px', fontWeight: '500' }}>
              Redirecting to dashboard...
            </Text>
          </div>

          {submitting && <Spin />}
        </div>
      </div>
    );
  }

  // Question Screen
  const currentQuestion = getCurrentQuestion();
  
  return (
    <div style={{ 
      minHeight: '100vh',
      background: '#f8fafc',
      padding: '20px 20px 40px'
    }}>
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          background: 'white',
          borderRadius: '8px',
          padding: '24px 32px',
          marginBottom: '20px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <Text style={{ color: '#64748b', fontSize: '14px', display: 'block' }}>
              Question {currentQuestionIndex + 1} of {questions.length}
            </Text>
            <Text style={{ color: '#1e293b', fontSize: '18px', fontWeight: '600' }}>
              Profiling Assessment
            </Text>
          </div>
          <div style={{ textAlign: 'right' }}>
            <Text style={{ color: '#64748b', fontSize: '14px', display: 'block' }}>
              {getProgress()}% complete
            </Text>
            <div style={{
              width: '100px',
              height: '8px',
              background: '#e2e8f0',
              borderRadius: '4px',
              overflow: 'hidden',
              marginTop: '4px'
            }}>
              <div style={{
                background: '#ff5722',
                height: '100%',
                width: `${getProgress()}%`,
                borderRadius: '4px',
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>
        </div>

        {/* Question Card */}
        <div style={{ 
          background: 'white',
          borderRadius: '8px',
          padding: '40px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          marginBottom: '20px'
        }}>
          <div style={{ marginBottom: '32px' }}>
            <Title level={2} style={{ 
              color: '#1e293b',
              margin: '0 0 8px 0',
              fontSize: '24px',
              fontWeight: '600',
              lineHeight: '1.3'
            }}>
              {currentQuestion.text}
            </Title>
            
            <Text style={{ fontSize: '13px', color: '#94a3b8' }}>
              {currentQuestion.question_code}
            </Text>
          </div>

          {renderQuestionInput(currentQuestion)}
        </div>

        {/* Navigation */}
        <div style={{ 
          background: 'white',
          borderRadius: '8px',
          padding: '24px 32px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <Button
            size="large"
            onClick={handlePreviousQuestion}
            disabled={currentQuestionIndex === 0}
            style={{
              height: '44px',
              minWidth: '100px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              border: '1px solid #cbd5e1',
              background: 'white',
              color: '#475569'
            }}
            icon={<Icon type="arrow-left" />}
          >
            Previous
          </Button>

          {!isLastQuestion() ? (
            <Button
              type="primary"
              size="large"
              onClick={() => handleNextQuestion()}
              disabled={!isCurrentQuestionAnswered()}
              style={{
                height: '44px',
                minWidth: '100px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                background: isCurrentQuestionAnswered() ? '#ff5722' : '#cbd5e1',
                borderColor: isCurrentQuestionAnswered() ? '#ff5722' : '#cbd5e1',
                boxShadow: 'none'
              }}
              icon={<Icon type="arrow-right" />}
            >
              Next
            </Button>
          ) : (
            <Button
              type="primary"
              size="large"
              onClick={handleSubmit}
              disabled={!isCurrentQuestionAnswered()}
              loading={submitting}
              style={{
                height: '44px',
                minWidth: '120px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                background: isCurrentQuestionAnswered() ? '#00bf63' : '#cbd5e1',
                borderColor: isCurrentQuestionAnswered() ? '#00bf63' : '#cbd5e1',
                boxShadow: 'none'
              }}
              icon={!submitting && <Icon type="check" />}
            >
              Submit
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfiling;
